import { useEffect, useMemo, useState } from "react";
import type { configurationTypes } from "@repo/models";
import { Input, SelectInput, SwitchSelect } from "@repo/ui";
import { useWorkflowCreationContext } from "~/pages/administration/workflows-page/workflow-creation-context-provider";
import { isFieldType } from "~/pages/administration/workflows-page/workflows-dialog/views/metadata-view/utils/field-type-guards";
import type { CreateFieldRequest, Field } from "~/types/api";
import {
  hasErrors,
  minItems,
  required,
  validate,
  type ValidationErrors,
  type ValidationSchema,
} from "~/utils/validation";
import { ChoicesSection } from "./choices-section";
import { WorkflowsInputControl } from "../../workflows-input-control";

type FieldType = configurationTypes.components["schemas"]["FieldType"];
type FieldUnion = CreateFieldRequest | Field;
export type FieldTypeOption = Exclude<FieldType, "Directory">;

const UI_TYPES = {
  String: "String",
  Integer: "Integer",
  Directory: "Directory",
  Choices: "Choices",
} as const;

type FieldFormSchema = {
  name: string;
  options: { value: string; default: boolean }[];
};

const getValidationSchema = (
  isChoicesMode: boolean,
  existingNames: string[]
): ValidationSchema<FieldFormSchema> => ({
  name: [
    required("Field name cannot be empty."),
    {
      condition: (v) =>
        existingNames.some((n) => n.trim().toLowerCase() === v.trim().toLowerCase()),
      message: "Use a different name than existing.",
    },
  ],
  ...(isChoicesMode && {
    options: [minItems(2, "Please provide at least two choices.")],
  }),
});

type Props = {
  field: FieldUnion | null;
  onSave: (updatedField: FieldUnion) => void;
  existingNames?: string[];
  onHasUnsavedChanges?: (hasChanges: boolean, onDiscard: () => void) => void;
  workflowId?: number | null;
  isUpgradeMode?: boolean;
};

export const MetadataInput = ({
  field,
  onSave,
  existingNames = [],
  onHasUnsavedChanges,
}: Props) => {
  const [localCopy, setLocalCopy] = useState<FieldUnion | null>(field);
  const [originalField, setOriginalField] = useState<FieldUnion | null>(field);
  const [originalIsChoicesMode, setOriginalIsChoicesMode] = useState(
    (field?.options?.length ?? 0) > 0
  );
  const [isChoicesMode, setIsChoicesMode] = useState(originalIsChoicesMode);
  const [saveCount, setSaveCount] = useState(0);
  const [errors, setErrors] = useState<ValidationErrors<FieldFormSchema>>({});

  const {
    state: { sourceWorkflowId, isUpgradeMode },
  } = useWorkflowCreationContext();

  const hasUnsavedChanges = useMemo(() => {
    if (!originalField || !localCopy) return false;
    if (isChoicesMode !== originalIsChoicesMode) return true;
    return JSON.stringify(originalField) !== JSON.stringify(localCopy);
  }, [originalField, localCopy, isChoicesMode, originalIsChoicesMode]);

  const handleSaveInternal = () => {
    if (!localCopy) return;

    const validationErrors = validate<FieldFormSchema>(
      {
        name: localCopy.name,
        options: localCopy.options ?? [],
      },
      getValidationSchema(isChoicesMode, existingNames)
    );

    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    const objectToSave: FieldUnion = {
      ...localCopy,
      options: isChoicesMode ? localCopy.options || [] : [],
    };
    setOriginalField(objectToSave);
    setOriginalIsChoicesMode(isChoicesMode);
    setSaveCount((c) => c + 1);
    onSave(objectToSave);
  };

  const handleCancelInternal = () => {
    if (field) {
      setLocalCopy(field);
      setIsChoicesMode((field.options?.length ?? 0) > 0);
      setErrors({});
    }
  };

  useEffect(() => {
    onHasUnsavedChanges?.(hasUnsavedChanges, handleCancelInternal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented) return;
    switch (e.key) {
      case "Escape":
        e.stopPropagation();
        handleCancelInternal();
        break;
      case "Enter":
        if ((e.target as HTMLElement).tagName === "TEXTAREA") return;
        e.stopPropagation();
        e.preventDefault();
        handleSaveInternal();
        break;
    }
  };

  const currentUiType = isChoicesMode
    ? "Choices"
    : localCopy?.type === "Directory"
      ? "Directory"
      : localCopy?.type;

  if (!localCopy) return <div className="p-4 text-gray-400">Select a field to edit</div>;

  return (
    <div className="relative flex flex-col gap-2 border-b border-b-gray-300 pb-12">
      <div className="flex flex-col gap-7">
        <div className="flex justify-between gap-4" onKeyDown={handleKeyDown}>
          <Input
            label="Field Name"
            value={localCopy.name}
            error={errors.name}
            onChange={(e) => {
              const newName = e.target.value;
              setLocalCopy({ ...localCopy, name: newName });
              if (errors.name) {
                const nameErrors = getValidationSchema(isChoicesMode, existingNames).name ?? [];
                const stillInvalid = nameErrors.some((r) => r.condition(newName));
                if (!stillInvalid) setErrors({ ...errors, name: undefined });
              }
            }}
          />
          <SelectInput
            label="Field Type"
            options={Object.values(UI_TYPES).map((t) => ({ label: t, value: t }))}
            value={currentUiType}
            clearable={false}
            onChange={(value) => {
              if (value === "Choices") {
                setIsChoicesMode(true);
                setLocalCopy((prev) => ({
                  ...prev!,
                  type: prev!.type === "Directory" ? "String" : prev!.type,
                }));
              } else {
                setIsChoicesMode(false);
                setLocalCopy({
                  ...localCopy,
                  type: value as FieldType,
                  options: [],
                });
              }
            }}
          />
          <SwitchSelect
            containerClassName="flex-1"
            label="Required?"
            checked={localCopy.required}
            onCheckedChange={(checked) => setLocalCopy({ ...localCopy, required: checked })}
          />
        </div>
        {isChoicesMode && (
          <ChoicesSection
            type={localCopy.type}
            options={localCopy.options ?? []}
            saveCount={saveCount}
            error={errors.options}
            workflowId={isUpgradeMode ? sourceWorkflowId : null}
            fieldId={isFieldType(localCopy) ? localCopy.id : null}
            onTypeChange={(newType, clearedOptions) => {
              setLocalCopy({
                ...localCopy,
                type: newType,
                options: clearedOptions ?? localCopy.options ?? [],
              });
              if (clearedOptions) setSaveCount((c) => c + 1);
            }}
            onOptionsChange={(options) => setLocalCopy({ ...localCopy, options })}
            onErrorClear={() => setErrors((prev) => ({ ...prev, options: undefined }))}
            isUpgradeMode={isUpgradeMode}
          />
        )}
      </div>
      {hasUnsavedChanges && (
        <div className="absolute right-0 bottom-3 ml-auto flex gap-2">
          <WorkflowsInputControl controlType="cancel" onClick={handleCancelInternal} />
          <WorkflowsInputControl controlType="confirm" onClick={handleSaveInternal} />
        </div>
      )}
    </div>
  );
};
